import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { trackEvent } from '../../../client/analytics';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

const EVENT_IDS = {
  VIEW_IMPRECISE_LOCATION_ALERT: '7944',
  VIEW_MAP_PIN_ADJUSTMENT_MODAL: '7945',
  CLICK_CORRECT_MAP_PIN: '7946',
  CLICK_REPORT_MAP_PIN: '7947',
  CLICK_SUBMIT_MAP_PIN: '7948',
} as const;

// https://dataportal.zillowgroup.net/event-registry/7944
const ViewImpreciseLocationAlert = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.VIEW_IMPRECISE_LOCATION_ALERT,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_address_confirmation_modal',
      trigger_type_nm: 'view',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'zrm_address_confirmation_modal',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7945
const ViewMapPinAdjustmentModalFromPostingPath = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.VIEW_MAP_PIN_ADJUSTMENT_MODAL,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'view_zrm_fix_property_location_alert',
      trigger_type_nm: 'view',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'view_zrm_fix_property_location_alert',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: [],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7946
const ClickCorrectMapPinLocationFromPostingPath = ({
  user,
  currentProperty,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_CORRECT_MAP_PIN,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'correct_map_pin_location',
      trigger_source_nm: 'zrm_correct_map_pin_alert',
    },
    semantic: {
      semantic_event_nm: 'correct_map_pin_location',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/7947
const ClickReportMapPinLocationFromPropertyDashboard = ({
  user,
  currentProperty,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_REPORT_MAP_PIN,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_property_overview',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zrm_single_property_dashboard_overflow_menu',
      trigger_source_nm: 'report_map_pin_location',
    },
    semantic: {
      semantic_event_nm: 'correct_map_pin_location',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/7948
const ClickSubmitMapPinAdjustmentRequest = ({
  user,
  currentProperty,
  triggerLocation,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
  triggerLocation: 'posting_path' | 'zrm_property_overview';
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_SUBMIT_MAP_PIN,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'submit_ticket',
      trigger_source_nm: 'zrm_submit_location_adjustment_modal',
    },
    semantic: {
      semantic_event_nm: 'submit_map_pin_ticket_zrm',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

const CorrectMapPinCSEvents = {
  ViewImpreciseLocationAlert,
  ViewMapPinAdjustmentModalFromPostingPath,
  ClickCorrectMapPinLocationFromPostingPath,
  ClickReportMapPinLocationFromPropertyDashboard,
  ClickSubmitMapPinAdjustmentRequest,
};

// Clickstream visualizer data-eventid attributes
ViewImpreciseLocationAlert.data_event_id = EVENT_IDS.VIEW_IMPRECISE_LOCATION_ALERT;
ViewMapPinAdjustmentModalFromPostingPath.data_event_id = EVENT_IDS.VIEW_MAP_PIN_ADJUSTMENT_MODAL;
ClickCorrectMapPinLocationFromPostingPath.data_event_id = EVENT_IDS.CLICK_CORRECT_MAP_PIN;
ClickReportMapPinLocationFromPropertyDashboard.data_event_id = EVENT_IDS.CLICK_REPORT_MAP_PIN;
ClickSubmitMapPinAdjustmentRequest.data_event_id = EVENT_IDS.CLICK_SUBMIT_MAP_PIN;

export default CorrectMapPinCSEvents;
